import { useCommandState } from "cmdk";
import { useRef, type ReactNode } from "react";

import type { LinkTarget } from "@/features/collaboration/lib/links";
import { useWorkspaceSearch } from "@/features/search/api";
import { SearchResultGroups } from "@/features/search/components/SearchResultGroups";
import { useDebouncedValue } from "@/features/search/hooks/useDebouncedValue";
import { useSelectFirstResult } from "@/features/search/hooks/useSelectFirstResult";
import { SEARCH_DEBOUNCE_MS, isSearchable } from "@/features/search/lib/query";

interface WorkspaceSearchProps {
  /** Go to a result and close the palette. */
  onOpen: (target: LinkTarget) => void;
  /** The palette's "No results", held back while the server is still searching. */
  empty: ReactNode;
}

/**
 * Server search inside the command palette: once the query has two characters it asks the
 * server (200 ms after typing stops) and lists projects, tasks, clients and people. Render it
 * inside a <Command filter={paletteFilter}>; it reads the query from the palette's input.
 */
export function WorkspaceSearch({ onOpen, empty }: WorkspaceSearchProps) {
  const query = useCommandState((state) => state.search);
  const settled = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const search = useWorkspaceSearch(settled);
  const searching = isSearchable(query);
  const loading = searching && (settled.trim() !== query.trim() || search.isFetching);
  const results = searching && isSearchable(settled) ? search.data : undefined;
  const anchorRef = useRef<HTMLSpanElement>(null);
  useSelectFirstResult(anchorRef, results, loading);

  return (
    <>
      <span ref={anchorRef} hidden />
      {searching ? (
        <SearchResultGroups results={results} loading={loading} onOpen={onOpen} />
      ) : null}
      {loading ? null : empty}
    </>
  );
}
